import { ModerationStoreSlice, BlockReasons } from './types';
import { reduxKey } from './constants';
import { PapiDeviation } from '@wix/da-papi-types';
import { getIsLoggedIn, getShowMatureContent } from '../../publicSession/selectors';
import { createSelector } from 'reselect';

function getModerationState(state) {
  return (state as ModerationStoreSlice)?.[reduxKey] || { deviations: {}, users: {} };
}
/**
 * @deprecated Used only for stories
 */
export function getIsUserBlocked(state, userId: number, defaultValue = false) {
  const userState = getModerationState(state).users[userId];
  return userState ? userState.isBlocked : defaultValue;
}

const getUserModerationStateById = createSelector(
  state => state,
  (state, userId) => userId,
  (state, userId) => getModerationState(state).users[userId]
);

const getDeviationModerationStateById = createSelector(
  state => state,
  (state, deviationId) => deviationId,
  (state, deviationId) => getModerationState(state).deviations[deviationId]
);

/**
 * Recreates the block reasons for deviation on client side.
 * Ingnores the isBlocked field in deviation as it can be stale.
 *
 * Uses moderation state for user and deviation to build correct state
 * on client.
 *
 * For mature and antisocial deviations adds the reasons in cached deviations
 * if they are missing.
 */
export const getBlockTypesForDeviation = createSelector(
  state => state,
  (state, deviation) => deviation,
  (state, deviation: PapiDeviation) => {
    if (!deviation) {
      return [];
    }

    const { blockReasons = [], isAntisocial, isMature } = deviation;
    let types = [...blockReasons] as BlockReasons[];

    const userModState = getUserModerationStateById(state, deviation.author?.userId);
    if (userModState) {
      const isUserBlocked = types.includes(BlockReasons.ArtistBlocked);

      // user blocked the artist on the client side
      if (!isUserBlocked && userModState.isBlocked) {
        types.push(BlockReasons.ArtistBlocked);
      }
      // user unblocked the artist on the client side
      if (isUserBlocked && !userModState.isBlocked) {
        types = types.filter(t => t !== BlockReasons.ArtistBlocked);
      }
    }

    const deviationModState = getDeviationModerationStateById(state, deviation.deviationId);
    if (deviationModState) {
      const isDeviationBlocked = types.includes(BlockReasons.DeviationBlocked);
      if (deviationModState.isBlocked && !isDeviationBlocked) {
        types.push(BlockReasons.DeviationBlocked);
      }

      // if it was deviation blocked, we override that
      if (!deviationModState.isBlocked && isDeviationBlocked) {
        types = types.filter(t => t !== BlockReasons.DeviationBlocked);
      }
    }

    // check for mature filter client side
    if (
      isMature &&
      !getShowMatureContent(state) &&
      !types.includes(BlockReasons.MatureFilter) &&
      !types.includes(BlockReasons.Nsfg)
    ) {
      types.push(BlockReasons.MatureFilter);
    }

    // extra check for anti-social because inside a saved editor
    // deviation we can have stale isBlocked value
    if (isAntisocial && !getIsLoggedIn(state) && !types.includes(BlockReasons.Antisocial)) {
      types.push(BlockReasons.Antisocial);
    }

    return types;
  }
);

const blockReasonPriority = [
  BlockReasons.Antisocial,
  BlockReasons.MatureLoggedOut,
  BlockReasons.BlockedByArtist,
  BlockReasons.ArtistBlocked,
  BlockReasons.DeviationBlocked,
  BlockReasons.StrictMatureAge,
  BlockReasons.MatureAge,
  BlockReasons.MatureFilter,
  BlockReasons.Nsfg, // matures that are also nsfg are mature first
];

export const getHighestPriorityBlockReasonForDeviation = createSelector(
  state => state,
  (state, deviation) => deviation,
  (state, deviation) => {
    const blockReasons = getBlockTypesForDeviation(state, deviation);
    return blockReasonPriority.find(reason => blockReasons.includes(reason));
  }
);

export const matureBlockReasons = [
  BlockReasons.MatureAge,
  BlockReasons.MatureFilter,
  BlockReasons.MatureLoggedOut,
  BlockReasons.StrictMatureAge,
  BlockReasons.Nsfg,
];

export function getIsMatureBlock(blockReason?: BlockReasons) {
  return !!blockReason && matureBlockReasons.includes(blockReason);
}
